import { mapGetters, mapActions } from 'vuex';
import Vue from 'vue';
import debounce from '@/helpers/debounce';
import breakpoints from '@/helpers/breakpoints';
import templateBuilder from '@/helpers/templateBuilder';
import request from '@/helpers/request';
import Sidebar from '@/components/Sidebar/index.vue';
import TabLoader from '@/components/TabLoader/index.vue';
import ProductMapList from '@/components/ProductMapList/index.vue';
import OverviewContainer from '@/containers/OverviewContainer/index.vue';
import CompatibleDevices from '@/containers/CompatibleDevices/index.vue';
import Accessories from '@/containers/Accessories/index.vue';
import PlusIcon from '@/assets/icons/plus.svg';
import MinusIcon from '@/assets/icons/minus.svg';
import PcRenderContent from '../../../node_modules/@garmin/components-product/src/components/PcRenderContent';

// need to be registered globally so pc-render-content can render them
Vue.component('overview-container', OverviewContainer);
Vue.component('compatible-devices', CompatibleDevices);
Vue.component('accessories', Accessories);

export default {
  name: 'TabsContent',
  props: {
    activeTabId: {
      type: String,
      required: true,
    },
    tabContent: {
      type: Array,
      required: true,
    },
    tabIds: {
      type: Array,
      required: true,
    },
    tabLabels: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      showAllSections: false,
      onResizeFn: null,
      loaderInstance: null,
      cardsListInstance: null,
      renderContent: false,
      observer: null,
    };
  },
  mounted() {
    // check if IntersectionObserver is available if not, set renderContent to true by default
    if (window.IntersectionObserver) {
      // Setup the IntersectionObserver for watching for this component to be in view
      const options = {
        threshold: [0.0],
      };
      const callback = (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.renderContent = true;
            observer.unobserve(entry.target);
          }
        });
      };

      this.observer = new IntersectionObserver(callback, options);

      // Start watching for this component to be in view
      this.observer.observe(this.$el);
    } else {
      this.renderContent = true;
    }

    if (window.location.hash) {
      window.addEventListener('load', this.scrollToActiveTab, false);
    }
    // Register an event listener when the Vue component is ready
    this.showAllSections = this.calculateDisplayMode(window);
    this.onResizeFn = debounce(this.onResize, 200);

    // Bind the change event to the root element to make sure it exists. It will bubble up
    this.$refs.tabContent.addEventListener('change', this.mapsSelectHandler);

    window.addEventListener('resize', this.onResizeFn);
  },
  beforeDestroy() {
    // Remove the watcher for loading this component
    if (this.observer) {
      this.observer.unobserve(this.$el);
    }

    // Unregister the event listener before destroying this Vue instance
    window.removeEventListener('resize', this.onResizeFn);
    this.$refs.tabContent.removeEventListener('change', this.mapsSelectHandler);

    // Unregister the load event
    window.removeEventListener('load', this.scrollToActiveTab, false);
  },
  watch: {
    partNumber() {
      // Start watching for this component to be visible.
      if (this.observer) {
        this.observer.observe(this.$el);
        this.renderContent = false;
      }

      // Reset the maps when changing products
      const regionSelect = document.querySelector('#js__regionSelect');
      const activitySelect = document.querySelector('#js__activitySelect');
      if (regionSelect && activitySelect) {
        regionSelect.selectedIndex = 0;
        activitySelect.selectedIndex = 0;
      }

      if (this.cardsListInstance) {
        this.cardsListInstance.products = [];
        this.cardsListInstance.showNoResults = false;
      }
    },
  },
  computed: {
    tabsContentClass() {
      return this.displayNewOverviewTab ? 'app__tabs__content__full' : 'app__tabs__content';
    },
    tabsIconClass() {
      return 'app__tabs__content__link__icon';
    },
    tabsIconPlusClass() {
      return `${this.tabsIconClass} ${this.tabsIconClass}--plus`;
    },
    tabsIconMinusClass() {
      return `${this.tabsIconClass} ${this.tabsIconClass}--minus`;
    },
    displayNewOverviewTab() {
      return this.newOverviewTab && this.activeTabId === 'overview';
    },
    ...mapGetters({
      translations: 'getTranslations',
      partNumber: 'getPartNumber',
      mapsUrlTemplate: 'getMapsJsonUrl',
      newOverviewTab: 'getNewOverviewTab',
      defaultProductTab: 'getDefaultProductTab',
    }),
  },
  methods: {
    setTabsGaAttr(hash) {
      return `EMS - Content Tab, open, ${hash}`;
    },
    ...mapActions([
      'setProductActiveTabId',
    ]),
    isHeaderLinkActive(tabId) {
      return {
        'app__tabs__content__link--active': tabId === this.activeTabId,
      };
    },
    currentTabClass(tabId) {
      return `app__tabs__content__${tabId}`;
    },
    shouldShowTabTitle(tabId) {
      if (this.showAllSections) return true;
      return this.activeTabId === tabId;
    },
    shouldShowTabContent(tabId) {
      return this.activeTabId === tabId;
    },
    calculateDisplayMode(target) {
      return target.innerWidth < breakpoints['screen-sm'];
    },
    onResize(event) {
      this.showAllSections = this.calculateDisplayMode(event.target);

      // Reset to overview tab if no active tab when switching to desktop
      if (!this.activeTabId && !this.showAllSections) {
        this.setProductActiveTabId({ tabId: this.defaultProductTab.id });
      }
    },
    injectMapsComponents(container) {
      // Build the loader component
      if (!this.loaderInstance) {
        const TabLoaderComp = Vue.extend(TabLoader);
        this.loaderInstance = new TabLoaderComp({
          propsData: { show: false },
        });
        this.loaderInstance.$mount();
      }

      if (!this.cardsListInstance) {
        // Build the cards list component
        const MapList = Vue.extend(ProductMapList);
        this.cardsListInstance = new MapList({
          propsData: {
            products: [],
            showNoResults: false,
            noResultsMessage: this.translations.mapsTabNoMaps,
          },
          store: this.$store,
        });
        this.cardsListInstance.$mount();
      }

      if (!container.children.length) {
        this.cardsListInstance.products = [];
        this.cardsListInstance.showNoResults = false;

        // Inject the loader and the cards list into the container
        container.appendChild(this.loaderInstance.$el);
        container.appendChild(this.cardsListInstance.$el);
      }
    },
    mapsSelectHandler() {
      const container = document.querySelector('#compatibleMaps');
      const regionSelect = document.querySelector('#js__regionSelect');
      const activitySelect = document.querySelector('#js__activitySelect');

      if (!container || !regionSelect || !activitySelect) return;

      // Inject the needed components
      this.injectMapsComponents(container);

      // We need both select inputs to have values before calling the API
      if (!regionSelect.selectedIndex || !activitySelect.selectedIndex) {
        return;
      }

      // Show the loader and clear previous products
      this.loaderInstance.show = true;
      this.cardsListInstance.products = [];
      this.cardsListInstance.showNoResults = false;

      // Build the URL to query from
      const url = templateBuilder(this.mapsUrlTemplate, {
        partNum: this.partNumber,
        region: regionSelect.options[regionSelect.selectedIndex].value,
        activity: activitySelect.options[activitySelect.selectedIndex].value,
      });

      // Make the API call
      request({
        url,
      }).then((data) => {
        // Add the API data to the cards list component to render
        this.cardsListInstance.products = data;
        this.cardsListInstance.showNoResults = true;
      }).catch(() => {
        // If the API fails show the no results message
        this.cardsListInstance.showNoResults = true;
      }).finally(() => {
        // Always hide the loader
        this.loaderInstance.show = false;
      });
    },
    scrollToActiveTab() {
      const activeTab = window.location.hash.slice(1);
      // For mobile view, scroll to active tab
      if (window.screen.width <= breakpoints['screen-sm']) {
        window.scrollTo(0, this.$refs[activeTab][0].offsetTop);
      }
    },
    selectTab(tabId) {
      this.setProductActiveTabId({ tabId });
      // Scroll the container into view
      setTimeout(() => {
        const elToScroll = this.$refs[tabId][0];
        if (elToScroll) elToScroll.scrollIntoView(true);
      }, 50);
    },
    toggleTab(tabId) {
      // If already active hide it
      if (this.activeTabId === tabId) {
        this.setProductActiveTabId({ tabId: '' });
        return;
      }

      // If another tab is clicked select it
      this.selectTab(tabId);
    },
  },
  components: {
    Sidebar,
    PcRenderContent,
    PlusIcon,
    MinusIcon,
  },
};
